import { create } from 'zustand';
import type { CallMedia } from '@/services/calls/types';
import type { ParticipantState } from '@/lib/calls/jitsi-room';

/**
 * Call UI state.
 *
 * This store holds only the serializable/observable state the call UI renders.
 * The imperative WebRTC objects (RTCPeerConnection, signaling) live in the
 * call engine (`lib/calls/call-engine.ts`); media streams are kept here purely
 * so the overlay components can attach them to <video>/<audio> elements.
 *
 * Not persisted — a call must never survive a page reload.
 */

/** Lifecycle phase of the active call. */
export type CallPhase =
  | 'incoming' // inbound call ringing, awaiting accept/decline
  | 'outgoing' // outbound call placed, awaiting answer
  | 'connecting' // answered, peer connection negotiating
  | 'connected' // media flowing
  | 'ended'; // terminal — shown briefly before the store resets

export interface ActiveCall {
  /** Stable id correlating every signal of this call (the invite's unique_message_id). */
  callId: string;
  /**
   * Transport. `'p2p'` runs the in-house WebRTC engine (1-on-1 only); `'group'`
   * defers to a Jitsi room rendered by `GroupCallWindow` (matches Android,
   * which routes `chatMode === 'group'` chats to `JitsiActivity`).
   */
  kind: 'p2p' | 'group';
  /** Jitsi conference room id (group calls only — typically the chat room id). */
  roomId?: string;
  /** Pre-fetched Jitsi JWT for the group room. */
  jitsiToken?: string;
  /**
   * Server-assigned chat message id of the call invite. Sent as `call_data.id`
   * in every signal — the native client ignores call signals whose
   * `call_data.id` is 0 when the chat message already exists. Null until known
   * (for an outgoing call it's captured from the invite echo).
   */
  serverMessageId: number | null;
  /** The other party's user id. */
  peerId: number;
  /** The other party's display name. */
  peerName: string;
  /** The other party's avatar URL (may be empty). */
  peerImage: string;
  /** Who initiated the call. */
  direction: 'incoming' | 'outgoing';
  /** Voice-only or voice + video. */
  media: CallMedia;
  phase: CallPhase;
  /** Local microphone muted. */
  isMuted: boolean;
  /** Local camera disabled (video calls only). */
  isCameraOff: boolean;
  /** Local user is screen-sharing instead of (or in addition to) camera. */
  isScreenSharing: boolean;
  /** Epoch ms when the call connected, or null while still negotiating. */
  connectedAt: number | null;
  /**
   * Epoch ms when this call record was created (set in setCall). Used by the
   * stale-call recovery path in `handleCallMessage` / `startCall`: a call
   * stuck in a non-progressing phase past the ring timeout is treated as
   * orphaned so it can't poison the next attempt with a phantom BUSY.
   */
  initiatedAt: number;
  /** Human-readable reason shown in the 'ended' phase. */
  endReason: string | null;
}

interface CallStoreState {
  /** The single active call, or null when idle. */
  call: ActiveCall | null;
  /** Local microphone/camera stream. */
  localStream: MediaStream | null;
  /** Remote party's stream. */
  remoteStream: MediaStream | null;

  /** Replace the active call (start a new one). */
  setCall: (call: ActiveCall) => void;
  /** Merge a partial update into the active call (no-op if idle). */
  patchCall: (updates: Partial<ActiveCall>) => void;
  setLocalStream: (stream: MediaStream | null) => void;
  setRemoteStream: (stream: MediaStream | null) => void;
  /**
   * Snapshot of every participant (local + remote) in a group call. Driven
   * by `JitsiRoom`'s `participantsChanged` event; the array is replaced
   * wholesale on every change so Zustand selectors fire correctly.
   */
  groupParticipants: ParticipantState[];
  setGroupParticipants: (next: ParticipantState[]) => void;
  /**
   * Group-call window mode. `'full'` shows the split view with sidebar
   * (matches WhatsApp's primary call window); `'mini'` is the floating
   * picture-in-picture used while the user is doing other things. Only
   * applies when `call.kind === 'group'`.
   */
  groupCallView: 'mini' | 'full';
  setGroupCallView: (view: 'mini' | 'full') => void;
  /** Clear all call state back to idle. */
  reset: () => void;
}

export const useCallStore = create<CallStoreState>((set, get) => ({
  call: null,
  localStream: null,
  remoteStream: null,
  groupParticipants: [],
  groupCallView: 'full',

  setCall: (call) => set({ call }),

  patchCall: (updates) => {
    const current = get().call;
    if (!current) return;
    set({ call: { ...current, ...updates } });
  },

  setLocalStream: (localStream) => set({ localStream }),
  setRemoteStream: (remoteStream) => set({ remoteStream }),

  setGroupParticipants: (groupParticipants) => set({ groupParticipants }),

  setGroupCallView: (groupCallView) => set({ groupCallView }),

  reset: () =>
    set({
      call: null,
      localStream: null,
      remoteStream: null,
      groupParticipants: [],
      groupCallView: 'full',
    }),
}));
